import { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiService, CallTrackerData } from '../services/api';

const pct = (v?: number | null, d = 0) => (v == null ? '–' : `${(v * 100).toFixed(d)}%`);
const oneIn = (p?: number | null) => (p == null || p <= 0 ? '' : p >= 0.5 ? '' : ` (1 in ${Math.round(1 / p)})`);
const STATUS_COLOR: Record<string, 'success' | 'error' | 'default'> = { hit: 'success', miss: 'error', open: 'default' };

export default function CallTracker() {
  const [data, setData] = useState<CallTrackerData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ source: '', target: 'SPY', direction: 'down', move_pct: '10', horizon_days: '14',
    rule: 'touches', made_on: new Date().toISOString().slice(0, 10), note: '' });
  const [preview, setPreview] = useState<number | null>(null);
  const set = (k: string) => (e: any) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => { apiService.getCalls().then(setData).catch(e => setError(e?.message || 'Failed to load')); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      if (!form.target || !(Number(form.move_pct) > 0) || !(Number(form.horizon_days) > 0)) return;
      apiService.getCallBaseRate(form.target, form.direction, Number(form.move_pct), Number(form.horizon_days), form.rule)
        .then(r => setPreview(r.base_rate)).catch(() => setPreview(null));
    }, 400);
    return () => clearTimeout(t);
  }, [form.target, form.direction, form.move_pct, form.horizon_days, form.rule]);

  const add = () => {
    if (!form.source.trim()) { setError('Who made the call?'); return; }
    apiService.addCall({ ...form, move_pct: Number(form.move_pct), horizon_days: Number(form.horizon_days),
      note: form.note || undefined }).then(d => { setData(d); setError(null); setForm(f => ({ ...f, note: '' })); })
      .catch(e => setError(e?.response?.data?.detail || 'Could not add the call'));
  };
  const remove = (id: number) => apiService.deleteCall(id).then(setData);
  const sources = data ? Object.keys(data.sources) : [];

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Call Tracker</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900 }}>
        Log a prediction — your guru's, a newsletter's or your own — and it is scored when its time is up, against how
        often the same move happens anyway. "SPY drops 10% within two weeks" happened from 1.8% of all start dates
        since 1928, about 1 in 57. A source whose calls come true much more often than their moves happen by chance is
        worth listening to; after 10 settled calls the page says whether that is clear yet.
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete freeSolo options={sources} value={form.source} sx={{ width: 180 }}
            onInputChange={(_, v) => setForm(f => ({ ...f, source: v }))}
            renderInput={p => <TextField {...p} size="small" label="Who called it" />} />
          <TextField size="small" label="Stock or SPY" value={form.target} onChange={set('target')} sx={{ width: 120 }} />
          <TextField size="small" select label="Goes" value={form.direction} onChange={set('direction')} sx={{ width: 100 }}>
            <MenuItem value="down">down</MenuItem><MenuItem value="up">up</MenuItem>
          </TextField>
          <TextField size="small" label="By at least (%)" type="number" value={form.move_pct} onChange={set('move_pct')} sx={{ width: 130 }} />
          <TextField size="small" label="Within (days)" type="number" value={form.horizon_days} onChange={set('horizon_days')} sx={{ width: 120 }} />
          <TextField size="small" select label="Counts if" value={form.rule} onChange={set('rule')} sx={{ width: 190 }}>
            <MenuItem value="touches">it gets there at any point</MenuItem>
            <MenuItem value="ends">it is there at the end</MenuItem>
          </TextField>
          <TextField size="small" label="Made on" type="date" value={form.made_on} onChange={set('made_on')}
            InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
          <TextField size="small" label="Note" value={form.note} onChange={set('note')} sx={{ minWidth: 180, flexGrow: 1 }} />
          <Button variant="contained" onClick={add}>Log the call</Button>
        </Box>
        {preview != null && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            This move happened anyway from {pct(preview, 1)} of start dates{oneIn(preview)} in {form.target.toUpperCase()}'s
            history{['SPY', 'MARKET'].includes(form.target.toUpperCase()) ? ' (the S&P 500 since 1928)' : ''}.
          </Typography>
        )}
      </Paper>

      {data && sources.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, overflowX: 'auto' }}>
          <Typography variant="subtitle1" fontWeight={600}>Sources</Typography>
          <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Who</TableCell><TableCell align="right">Calls</TableCell><TableCell align="right">Settled</TableCell>
                <TableCell align="right">Came true</TableCell><TableCell align="right">By chance</TableCell><TableCell>Verdict</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(data.sources).map(([src, s]) => (
                <TableRow key={src}>
                  <TableCell sx={{ fontWeight: 600 }}>{src}</TableCell>
                  <TableCell align="right">{s.calls}</TableCell>
                  <TableCell align="right">{s.settled}</TableCell>
                  <TableCell align="right">{s.hits} ({pct(s.hit_rate)})</TableCell>
                  <TableCell align="right">{s.expected_hits.toFixed(1)} ({pct(s.base_rate)})</TableCell>
                  <TableCell>
                    <Tooltip title={s.p_better_than_chance != null ? `chance of doing this well by luck: ${pct(s.p_better_than_chance, 1)}` : ''}>
                      <span>{s.verdict ?? `too early (${s.settled} of 10 settled)`}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {data && (
        <Paper sx={{ p: 2, overflowX: 'auto' }}>
          <Typography variant="subtitle1" fontWeight={600}>Calls</Typography>
          {data.calls.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No calls logged yet.</Typography>
          ) : (
            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', px: 1 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Made</TableCell><TableCell>Who</TableCell><TableCell>Call</TableCell>
                  <TableCell align="right">Happens anyway</TableCell><TableCell>Result</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.calls.map(c => (
                  <TableRow key={c.id} hover>
                    <TableCell>{c.made_on}</TableCell>
                    <TableCell>{c.source}</TableCell>
                    <TableCell>
                      {c.target} {c.direction} {c.move_pct}% {c.rule === 'touches' ? 'within' : 'after'} {c.horizon_days} days
                      {c.note && <Typography variant="caption" display="block" color="text.secondary">{c.note}</Typography>}
                    </TableCell>
                    <TableCell align="right">{pct(c.base_rate, 1)}{oneIn(c.base_rate)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={c.status} color={STATUS_COLOR[c.status]} />
                      {c.outcome_pct != null && <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                        {c.outcome_pct > 0 ? '+' : ''}{c.outcome_pct.toFixed(1)}%{c.resolved_on ? ` on ${c.resolved_on}` : ''}</Typography>}
                    </TableCell>
                    <TableCell><IconButton size="small" onClick={() => remove(c.id)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
    </Box>
  );
}
